import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminTable from '../components/AdminTable';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import { getAllContent, createContent, updateContent, deleteContent, getAllCategories } from '../api/apiService';

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [filterCategory, setFilterCategory] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  const fetchCategories = async () => {
    try {
      const data = await getAllCategories('Blog');
      setCategories(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const sortStr = order === 'desc' ? `-${orderBy}` : orderBy;
      const data = await getAllContent('Blog', page + 1, rowsPerPage, searchQuery, sortStr, filterCategory, dateFilter);
      setBlogs(data.data || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [page, rowsPerPage, searchQuery, orderBy, order, filterCategory, dateFilter]);

  const handleFilter = (type, value) => {
    if (type === 'category') setFilterCategory(value);
    if (type === 'date') setDateFilter(value);
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };


  const columns = [
    {
      id: 'image',
      label: 'Image',
      render: (value) => (
        <Avatar 
          src={getImageUrl(value)} 
          variant="rounded" 
          sx={{ width: 40, height: 40 }} 
          slotProps={{ img: { onError: handleImageError } }}
        />
      ),
    },
    { id: 'title', label: 'Title' },
    { id: 'category', label: 'Category' },
    { id: 'author', label: 'Author' },
    { id: 'date', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
    {
      id: 'status',
      label: 'Status',
      render: (value) => (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'inline-block',
            bgcolor: value === 'Published' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(102, 102, 102, 0.1)',
            color: value === 'Published' ? '#2E7D32' : '#666',
          }}
        >
          {value}
        </Box>
      ),
    },
  ];

  const handleOpen = (blog = null) => {
    if (blog) {
      setEditingBlog({ 
        ...blog, 
        content: blog.description || '',
        description: blog.shortDescription || '',
        status: blog.status === 'Active' ? 'Published' : blog.status
      });
      setImagePreview(blog.image);
    } else {
      setEditingBlog({ title: '', category: '', author: '', date: new Date().toISOString().split('T')[0], status: 'Draft', content: '', description: '' });
      setImagePreview('');
    }
    setImageFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingBlog(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('title', editingBlog.title);
    formData.append('category', editingBlog.category);
    formData.append('author', editingBlog.author);
    formData.append('date', editingBlog.date);
    formData.append('status', editingBlog.status === 'Published' ? 'Active' : editingBlog.status);
    formData.append('description', editingBlog.content);
    formData.append('shortDescription', editingBlog.description || '');
    formData.append('type', 'Blog');

    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      if (editingBlog.id) {
        await updateContent(editingBlog.id, formData);
      } else {
        await createContent(formData);
      }
      fetchBlogs();
      handleClose();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert(error.response?.data?.message || 'Error saving blog');
    }
  };

  const handleDelete = (blog) => {
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (blogToDelete) {
      try {
        await deleteContent(blogToDelete.id);
        fetchBlogs();
        setDeleteDialogOpen(false);
        setBlogToDelete(null);
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const filteredBlogs = blogs; // Now filtered on server

  return (
    <Box>
      <AdminTable
        title="Blogs Management"
        columns={columns}
        data={filteredBlogs}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        loading={loading}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onSearch={setSearchQuery}
        onSort={handleSort}
        onFilter={handleFilter}
        categories={categories}
        onAdd={() => handleOpen()}
        onEdit={(blog) => handleOpen(blog)}
        onDelete={handleDelete}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '16px', p: 1 },
          }
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {editingBlog?.id ? 'Edit Blog' : 'Add New Blog'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Blog Title"
                variant="outlined"
                value={editingBlog?.title || ''}
                onChange={(e) => setEditingBlog({ ...editingBlog, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Category"
                select
                variant="outlined"
                value={editingBlog?.category || ''}
                onChange={(e) => setEditingBlog({ ...editingBlog, category: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id || cat._id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Author"
                variant="outlined"
                value={editingBlog?.author || ''}
                onChange={(e) => setEditingBlog({ ...editingBlog, author: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Publication Date"
                type="date"
                variant="outlined"
                value={editingBlog?.date ? new Date(editingBlog.date).toISOString().split('T')[0] : ''}
                onChange={(e) => setEditingBlog({ ...editingBlog, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Status"
                select
                variant="outlined"
                value={editingBlog?.status || 'Draft'}
                onChange={(e) => setEditingBlog({ ...editingBlog, status: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Short Description"
                variant="outlined"
                multiline
                rows={2}
                value={editingBlog?.description || ''}
                onChange={(e) => setEditingBlog({ ...editingBlog, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <input
                type="file"
                accept="image/*"
                id="blog-image-upload"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              <label htmlFor="blog-image-upload">
                <Box
                  sx={{
                    border: '2px dashed #E0E0E0',
                    borderRadius: '12px',
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { borderColor: '#F25828', bgcolor: 'rgba(242, 88, 40, 0.02)' },
                  }}
                >
                  {imagePreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={imagePreview}
                        sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={(e) => {
                          e.preventDefault();
                          setImageFile(null);
                          setImagePreview('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Click to upload cover image or drag and drop
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        PNG, JPG or WebP (max. 2MB)
                      </Typography>
                    </>
                  )}
                </Box>
              </label>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
                Content
              </Typography>
              <Box sx={{ 
                '& .quill': { borderRadius: '10px', overflow: 'hidden' },
                '& .ql-toolbar': { borderTopLeftRadius: '10px', borderTopRightRadius: '10px', borderColor: '#E0E0E0' },
                '& .ql-container': { borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', borderColor: '#E0E0E0', minHeight: '200px' }
              }}>
                <ReactQuill
                  theme="snow"
                  value={editingBlog?.content || ''}
                  onChange={(content) => setEditingBlog({ ...editingBlog, content })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link', 'clean']
                    ],
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            sx={{ color: '#666', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#F25828',
              '&:hover': { bgcolor: '#d44a20' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            {editingBlog?.id ? 'Update Blog' : 'Save Blog'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Blog?"
        message={`Are you sure you want to delete "${blogToDelete?.title}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default AdminBlogs;
